export function slugify(text: string) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

export function hasAtLeastOneSocial(v: {
  website?: unknown;
  websiteUrl?: unknown;
  email?: unknown;
  businessEmail?: unknown;
  facebook?: unknown;
  facebookUrl?: unknown;
  instagram?: unknown;
  instagramUrl?: unknown;
  twitter?: unknown;
  twitterUrl?: unknown;
  x?: unknown;
  xUrl?: unknown;
  tiktok?: unknown;
  tiktokUrl?: unknown;
  youtube?: unknown;
  youtubeUrl?: unknown;
}) {
  const website = cleanString(v.website) || cleanString(v.websiteUrl);
  const email = cleanString(v.email) || cleanString(v.businessEmail);
  const fb = cleanString(v.facebook) || cleanString(v.facebookUrl);
  const ig = cleanString(v.instagram) || cleanString(v.instagramUrl);
  const tw =
    cleanString(v.twitter) ||
    cleanString(v.twitterUrl) ||
    cleanString(v.x) ||
    cleanString(v.xUrl);
  const tt = cleanString(v.tiktok) || cleanString(v.tiktokUrl);
  const yt = cleanString(v.youtube) || cleanString(v.youtubeUrl);

  return !!(website || email || fb || ig || tw || tt || yt);
}

function cleanString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function firstCleanString(...values: unknown[]) {
  for (const value of values) {
    const cleaned = cleanString(value);
    if (cleaned) return cleaned;
  }

  return "";
}

function isValidKenyanPhone(value: string) {
  const normalized = value.replace(/[\s()-]+/g, "");

  return (
    /^\+2547\d{8}$/.test(normalized) ||
    /^\+2541\d{8}$/.test(normalized) ||
    /^2547\d{8}$/.test(normalized) ||
    /^2541\d{8}$/.test(normalized) ||
    /^07\d{8}$/.test(normalized) ||
    /^01\d{8}$/.test(normalized)
  );
}

function normalizeWhatsAppCtaType(value: unknown) {
  return value === "catalog" ? "catalog" : "chat";
}

function normalizeWhatsAppPhoneDigits(value: unknown) {
  const raw = cleanString(value);
  if (!raw) return "";

  const cleaned = raw.replace(/[^\d+]/g, "");

  if (/^\+254[17]\d{8}$/.test(cleaned)) {
    return cleaned.replace(/[^\d]/g, "");
  }

  if (/^254[17]\d{8}$/.test(cleaned)) {
    return cleaned;
  }

  if (/^0[17]\d{8}$/.test(cleaned)) {
    return `254${cleaned.slice(1)}`;
  }

  if (/^[17]\d{8}$/.test(cleaned)) {
    return `254${cleaned}`;
  }

  const digits = cleaned.replace(/[^\d]/g, "");
  return /^\d{10,15}$/.test(digits) ? digits : "";
}

function isValidWhatsAppChatNumber(value: unknown) {
  return /^254[17]\d{8}$/.test(normalizeWhatsAppPhoneDigits(value));
}

function isValidWhatsAppCatalogUrl(value: unknown) {
  const raw = cleanString(value);
  if (!raw) return false;

  const withScheme = /^https?:\/\//i.test(raw)
    ? raw
    : /^(?:www\.)?wa\.me\//i.test(raw)
      ? `https://${raw}`
      : raw;

  return /^https?:\/\/(?:www\.)?wa\.me\/c\/\d{10,15}(?:[/?#].*)?$/i.test(
    withScheme
  );
}

/**
 * Pure onboarding validation shared by the owner app and trusted backend.
 * It intentionally has no React Native, Firebase client, or Admin SDK imports.
 */
export function validateBasics(d: any) {
  const location = d?.location || {};

  const ownerName = cleanString(d?.ownerName);
  const ownerPhoneRaw = cleanString(d?.ownerPhone);

  const name = cleanString(d?.name);

  const category = firstCleanString(
    d?.categoryName,
    d?.category,
    d?.categorySuggestionName,
    location?.categoryName
  );

  const phone = cleanString(d?.phone);

  const businessType = cleanString(d?.businessType);

  const addressLine = cleanString(d?.addressLine);

  const countyId = firstCleanString(d?.countyId, location?.countyId);

  const cityId = firstCleanString(d?.cityId, location?.cityId);
  const citySuggestionId = firstCleanString(
    d?.citySuggestionId,
    location?.citySuggestionId
  );

  const areaId = firstCleanString(d?.areaId, location?.areaId);
  const areaSuggestionId = firstCleanString(
    d?.areaSuggestionId,
    location?.areaSuggestionId
  );

  const locationPin = d?.locationPin;

  const ownerNameOk = !!ownerName;
  const ownerPhoneOk = !!ownerPhoneRaw;
  const ownerPhoneFormatOk = ownerPhoneOk && isValidKenyanPhone(ownerPhoneRaw);

  const nameOk = !!name;
  const categoryOk = !!category;
  const phonePresent = !!phone;
  const phoneOk = phonePresent && isValidKenyanPhone(phone);

  const businessTypeOk =
    businessType === "physical_shop" ||
    businessType === "freelancer" ||
    businessType === "online_only";

  const countyOk = !!countyId;

  const cityOk = !!cityId || !!citySuggestionId;
  const areaOk = !!areaId || !!areaSuggestionId;

  const locationAreaOk = countyOk && cityOk && areaOk;

  const addressOk = businessType === "physical_shop" ? !!addressLine : true;

  const locationPinConfirmedOk =
    businessType === "physical_shop"
      ? !!d?.hasConfirmedLocationPin &&
        typeof locationPin?.lat === "number" &&
        typeof locationPin?.lng === "number"
      : true;

  const ok =
    ownerNameOk &&
    ownerPhoneOk &&
    ownerPhoneFormatOk &&
    nameOk &&
    categoryOk &&
    phoneOk &&
    businessTypeOk &&
    locationAreaOk &&
    addressOk &&
    locationPinConfirmedOk;

  let reason = "";

  if (!ownerNameOk) reason = "Owner name is required";
  else if (!ownerPhoneOk) reason = "Owner phone is required";
  else if (!ownerPhoneFormatOk)
    reason = "Owner phone is required (Kenyan number)";
  else if (!nameOk) reason = "Business name is required";
  else if (!categoryOk) reason = "Category is required";
  else if (!phonePresent) reason = "Business phone is required";
  else if (!phoneOk) reason = "Business phone must be a valid Kenyan number";
  else if (!businessTypeOk) reason = "Business type is required";
  else if (!countyOk) reason = "County is required";
  else if (!cityOk) reason = "City / Town is required";
  else if (!areaOk) reason = "Area is required";
  else if (!addressOk) reason = "Shop / Office address is required";
  else if (!locationPinConfirmedOk)
    reason = "Location Pin is required for physical shop / office";

  return {
    ok,
    errors: {
      ownerName: !ownerNameOk,
      ownerPhone: !ownerPhoneOk || !ownerPhoneFormatOk,
      name: !nameOk,
      category: !categoryOk,
      phone: !phoneOk,
      businessType: !businessTypeOk,
      countyId: !countyOk,
      cityId: !cityOk,
      areaId: !areaOk,
      addressLine: businessType === "physical_shop" ? !addressOk : false,
      locationPin:
        businessType === "physical_shop" ? !locationPinConfirmedOk : false,
    },
    messages: {
      ownerPhone: !ownerPhoneOk
        ? "Owner phone is required"
        : !ownerPhoneFormatOk
          ? "Enter a valid Kenyan owner phone number"
          : "",
      phone: !phonePresent
        ? "Business phone is required"
        : !phoneOk
          ? "Enter a valid Kenyan business phone number"
          : "",
    },
    reason,
  };
}

export function validateBrand(d: any) {
  const logoOk = !!cleanString(d?.logoUrl);
  const whatsappCtaType = normalizeWhatsAppCtaType(d?.whatsappCtaType);
  const whatsappChatNumber = cleanString(d?.whatsappChatNumber);
  const whatsappCatalogUrl = cleanString(d?.whatsappCatalogUrl);
  const whatsappChatRequired = whatsappCtaType === "chat";
  const whatsappCatalogRequired = whatsappCtaType === "catalog";
  const whatsappChatValid = isValidWhatsAppChatNumber(whatsappChatNumber);
  const whatsappCatalogValid = isValidWhatsAppCatalogUrl(whatsappCatalogUrl);
  const waOk =
    (!whatsappChatRequired || whatsappChatValid) &&
    (!whatsappCatalogRequired || whatsappCatalogValid);

  const socialOk = hasAtLeastOneSocial({
    website: d?.website,
    websiteUrl: d?.websiteUrl,
    email: d?.email,
    businessEmail: d?.businessEmail,
    facebook: d?.facebook,
    facebookUrl: d?.facebookUrl,
    instagram: d?.instagram,
    instagramUrl: d?.instagramUrl,
    twitter: d?.twitter,
    twitterUrl: d?.twitterUrl,
    x: d?.x,
    xUrl: d?.xUrl,
    tiktok: d?.tiktok,
    tiktokUrl: d?.tiktokUrl,
    youtube: d?.youtube,
    youtubeUrl: d?.youtubeUrl,
  });

  return {
    ok: logoOk && waOk && socialOk,
    errors: {
      logoUrl: !logoOk,
      whatsappChatNumber: whatsappChatRequired && !whatsappChatValid,
      whatsappChatNumberMissing: whatsappChatRequired && !whatsappChatNumber,
      whatsappChatNumberInvalid:
        whatsappChatRequired && !!whatsappChatNumber && !whatsappChatValid,
      whatsappCatalogUrl: whatsappCatalogRequired && !whatsappCatalogValid,
      whatsappCatalogUrlMissing:
        whatsappCatalogRequired && !whatsappCatalogUrl,
      whatsappCatalogUrlInvalid:
        whatsappCatalogRequired &&
        !!whatsappCatalogUrl &&
        !whatsappCatalogValid,
      social: !socialOk,
    },
  };
}

export function validateItems(d: any) {
  const items = Array.isArray(d?.items) ? d.items : [];
  const itemErrors = items.flatMap((item: any, index: number) => {
    const name = cleanString(item?.name);
    const image =
      [
        item?.imageUrl,
        item?.imageUri,
        item?.imageURI,
        item?.img,
        item?.image,
        item?.photoUrl,
      ]
        .map(cleanString)
        .find(Boolean) || "";

    if (name && image) return [];

    return [
      {
        index,
        name,
        missingName: !name,
        missingImage: !image,
      },
    ];
  });
  const hasItems = items.length >= 1;
  const ok = hasItems && itemErrors.length === 0;

  return {
    ok,
    errors: {
      items: !hasItems,
      itemDetails: itemErrors.length > 0,
    },
    itemErrors,
  };
}

export function validateAll(d: any) {
  const b = validateBasics(d);
  const br = validateBrand(d);
  const it = validateItems(d);

  return {
    ok: b.ok && br.ok && it.ok,
    sections: {
      basics: b,
      brand: br,
      items: it,
    },
  };
}

export function getOnboardingMissingRequirements(validation: any) {
  const missing: string[] = [];

  const basics = validation?.sections?.basics;
  const brand = validation?.sections?.brand;
  const items = validation?.sections?.items;

  const basicsErrors = basics?.errors || {};
  const basicsMessages = basics?.messages || {};

  const basicsRequirements: [string, string][] = [
    ["ownerName", "Add the owner name."],
    [
      "ownerPhone",
      basicsMessages.ownerPhone || "Add a valid Kenyan owner phone number.",
    ],
    ["name", "Add the business name."],
    ["category", "Select or suggest a business category."],
    [
      "phone",
      basicsMessages.phone || "Add a valid Kenyan business phone number.",
    ],
    ["businessType", "Select a business type."],
    ["countyId", "Select a county."],
    ["cityId", "Select or suggest a city / town."],
    ["areaId", "Select or suggest an area."],
    ["addressLine", "Add the shop / office address."],
    ["locationPin", "Confirm the shop / office Location Pin."],
  ];

  basicsRequirements.forEach(([key, message]) => {
    if (basicsErrors[key]) {
      missing.push(message.endsWith(".") ? message : `${message}.`);
    }
  });

  if (
    basics &&
    !basics.ok &&
    !basicsRequirements.some(([key]) => basicsErrors[key])
  ) {
    missing.push(basics.reason || "Complete the Basics step.");
  }

  if (brand?.errors?.logoUrl) {
    missing.push("Add a business logo or profile/shopfront photo.");
  }

  if (brand?.errors?.whatsappChatNumber) {
    missing.push(
      "Add a valid WhatsApp chat number or switch the CTA to View Catalog on WhatsApp."
    );
  }

  if (brand?.errors?.whatsappCatalogUrl) {
    missing.push(
      "Add a valid WhatsApp Catalog URL or switch the CTA to Chat on WhatsApp."
    );
  }

  if (brand?.errors?.social) {
    missing.push("Add at least one website, email, or social link.");
  }

  if (items?.errors?.items) {
    missing.push("Add at least one product or service item.");
  }

  if (Array.isArray(items?.itemErrors)) {
    items.itemErrors.forEach((itemError: any) => {
      const itemNumber = Number(itemError?.index) + 1;
      const itemName = cleanString(itemError?.name);
      const itemLabel = itemName ? `\"${itemName}\"` : `Item ${itemNumber}`;

      if (itemError?.missingName && itemError?.missingImage) {
        missing.push(`${itemLabel} needs a name and image.`);
      } else if (itemError?.missingName) {
        missing.push(`${itemLabel} needs a name.`);
      } else if (itemError?.missingImage) {
        missing.push(`${itemLabel} needs an image.`);
      }
    });
  }

  return missing;
}
